import config from '../config.json';

const emptyPillClass = 'ds-header__navbar__nav__link__pill--empty';

// One page can show this count in more than one place: the Venue Cabinet tab bar renders its pill in
// Twig, the navbar builds its own, and below the tab bar the avatar menu holds a third. They all show
// the same number, so they are painted together from one request rather than fetching it each.
const TAB_BAR_PILL_SELECTOR = '.js-lead-hub-new-leads-pill';
const PILL_SELECTOR = `${config.LEADS_SELECTOR}, ${TAB_BAR_PILL_SELECTOR}`;

// Also read by an inline script beside the tab-bar pill in vc-navigation/index.twig, which paints the
// count before this module has loaded - renaming it here without there brings the flicker back.
const STORAGE_KEY = 'tgv.leadHub.newLeadsCount';

// The count is not on the page when it renders - counting new leads costs three queries, so it is
// fetched rather than rendered into the header - and a pill that appears a moment after the rest of
// the header reads as a flicker on every Venue Cabinet page a manager opens. The last count seen in
// this tab is painted straight away and the request only corrects it, which it rarely has to.
function readCachedCount() {
  try {
    const cached = window.sessionStorage.getItem(STORAGE_KEY);

    if (cached === null) {
      return null;
    }

    const count = Number(cached);

    return Number.isInteger(count) ? count : null;
  } catch {
    // A browser refusing storage costs the flicker back, nothing more.
    return null;
  }
}

function cacheCount(count) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, String(count));
  } catch {
    // As above - worth no interruption.
  }
}

function paint(pills, count) {
  pills.forEach(pill => {
    pill.textContent = count;
    pill.classList.toggle(emptyPillClass, count === 0);
  });
}

// The navbar re-renders on every breakpoint crossing, so without this a manager dragging a
// window open would issue one authenticated request per crossing. The count is fetched once
// and every later render repaints its freshly built pill from the same promise.
let newLeadsCountRequest = null;

function getNewLeadsCount() {
  if (!newLeadsCountRequest) {
    newLeadsCountRequest = fetch('/venue-cabinet/leads/new-leads-count', { credentials: 'same-origin' })
      .then(response => response.json())
      .then(({ count }) => {
        if (typeof count === 'number') {
          cacheCount(count);
          // The enquiries store subscribes with `immediate`, so it reads the count whether it
          // initialises before this lands or after.
          window.$?.publish?.('LEAD_HUB_NEW_LEADS', count);
        }

        return count;
      });
  }

  return newLeadsCountRequest;
}

export function updateLeadHubCount() {
  const pills = document.querySelectorAll(PILL_SELECTOR);

  if (!pills.length) {
    return;
  }

  const cachedCount = readCachedCount();

  if (cachedCount !== null) {
    paint(pills, cachedCount);
  }

  getNewLeadsCount()
    .then(count => {
      // A non-JSON response rejects above, but a JSON body without a numeric count would
      // otherwise render the word "undefined" in a pill that is no longer hidden.
      if (typeof count !== 'number') {
        return;
      }

      paint(pills, count);
    })
    // The pill is rendered hidden, so a failed count simply leaves it hidden — there is
    // nothing to recover and nothing worth interrupting the navbar render for.
    .catch(() => {});
}
